import { SaveClientFlags } from '@sdk/authentication/types';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { SUBSCRIPTION_LEVELS } from '@sdk/account/constants';

import getUserRoleName from '@web/common/utils/getUserRoleName';

const SLING_FORM_URL =
  'https://script.google.com/macros/s/AKfycbyjoNWnP3V4authesvha-1dCN4uogFmBKLZOmPpMSBYKRi4U5Nv4NnnGnstmzaJATWwQw/exec';
const TOAST_FORM_URL =
  'https://script.google.com/macros/s/AKfycbxQQPYIE3qWdxEEmbrA92ldBmDhIk3HxScYndNEyzcdzROs1FU1hFg8pzPe-xGB3HE0/exec';

const SURVEY_FLAG_KEY = 'subscriptionPreferenceSurveyDisplayed';

type SurveyFormData = {
  user_id: string;
  user_name: string;
  email: string;
  role: string;
  org_id: string;
  org_name: string;
  org_creation: string;
  industry: string;
  total_user_count: string;
  joined_user_count: string;
  location_count: string;
  toast_integration_status: string;
  reason: string;
  other: string;
  comment: string;
};

type SurveyFeedback = {
  reason: string;
  other: string;
  comment: string;
};

type AdditionalFeedbackData = {
  user: AuthUserType;
  saveClientFlags: SaveClientFlags;
  locationsCount: number;
  totalUserCount: number;
  joinedUserCount: number;
};

export function sendSurveyFeedback(
  feedback: SurveyFeedback,
  additionalData: AdditionalFeedbackData,
  isToastBilling: boolean,
  toastIntegrationStatus: string
) {
  const data = getFormData(feedback, additionalData, toastIntegrationStatus);
  const isValid = validateFeedback(feedback);
  const FORM_TO_SUBMIT = isToastBilling ? TOAST_FORM_URL : SLING_FORM_URL;

  if (isValid && !__DEV__) {
    fetch(`${FORM_TO_SUBMIT}?${new URLSearchParams(data)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  updateSurveyDisplayed(additionalData.saveClientFlags);
}

function getFormData(
  feedback: SurveyFeedback,
  additionalData: AdditionalFeedbackData,
  toastIntegrationStatus: string
): SurveyFormData {
  const { user, totalUserCount, joinedUserCount, locationsCount } = additionalData;
  const dismissedLabel = '**dismissed**';
  return {
    user_id: String(user.id),
    user_name: user.fullname,
    email: user.email,
    role: getUserRoleName(user.role),
    org_id: String(user.org.id),
    org_name: user.org.name,
    org_creation: String(user.org.createdAt).substr(0, 10),
    industry: user.org.businessCategory,
    total_user_count: String(totalUserCount),
    joined_user_count: String(joinedUserCount),
    location_count: String(locationsCount),
    toast_integration_status: toastIntegrationStatus || undefined,
    reason: feedback.reason || dismissedLabel,
    other: feedback.other || dismissedLabel,
    comment: feedback.comment || dismissedLabel,
  };
}

function validateFeedback(feedback: SurveyFeedback) {
  return !!feedback.reason?.length;
}

export function updateSurveyDisplayed(saveClientFlags: SaveClientFlags) {
  saveClientFlags({ flags: { [SURVEY_FLAG_KEY]: new Date().toISOString() } });
}

export function shouldCheckForSubscriptionPreferenceSurvey(
  user: AuthUserType,
  wasFreemiumSelected
) {
  const wasDisplayed = !!user.settings.clientFlags[SURVEY_FLAG_KEY];
  const isFreeCustomer = user.org.subscriptionLevel === SUBSCRIPTION_LEVELS.FREE;

  return !wasDisplayed && user.isManager && wasFreemiumSelected && isFreeCustomer;
}
